import * as React from 'react';
import { useState, useEffect } from 'react';
import EventBus from '../EventBus';
import EnergipresentationAccordion from './EnergipresentationAccordion';
import iEnergipresentationCircleModel from './iEnergipresentationCircle';
import iEnergipresentationGraphPropsModel from './iEnergipresentationGraphProps';
import iEnergipresentationApiRequestModel from "./RequestModels/iEnergipresentationApiRequest";
import iCircleData from './ResponseModels/iCircleData';
import iCircleSeriesItem from './ResponseModels/iCircleSeriesItem';
import iEnergipresentationYearsModel from './iEnergipresentationYears';
import iEnergipresentationMinMaxYearsModel from './iEnergipresentationMinMaxYears';
import DataHelper from './DataHelper';
import DisplayHelper from './DisplayHelper';
import * as Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/pattern-fill';
import EnergipresentationExportButton from './EnergipresentationExportButton';
import EnergipresentationOpenInNewTabButton from './EnergipresentationOpenInNewTabButton';

const EnergipresentationCircle = (props: iEnergipresentationGraphPropsModel) => {
    const initialState: iEnergipresentationCircleModel = {
        ...props,
        isLoading: true,
        year: "0",
    };

    const [delayed, setDelayed] = useState(true);
    const [state, setState] = useState<iEnergipresentationCircleModel>(initialState);

    useEffect(() => {
        setYears();
        EventBus.on("UpdateBlockYears_" + state.blockId, updateSelectedYears);
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setDelayed(false), 3000);
        return () => clearTimeout(timeout);
    }, []);

    const updateSelectedYears = async (years: iEnergipresentationYearsModel, data: iCircleData = null) => {
        if (!hasValues(state.circleData?.series) && !hasValues(data?.series)) {
            let customNewYears: iEnergipresentationYearsModel = { firstYear: years.firstYear };
            await getCircleData(customNewYears, updateSelectedYearFromApi);
            return;
        }

        let isLoading = false;
        if (!hasValues(state.circleData?.series) && hasValues(data?.series)) {
            updateState(data, years, isLoading);
            return;
        }

        let newState = { ...state };
        newState.isLoading = isLoading;
        setState(newState);
    };

    const hasValues = (list: Array<iCircleSeriesItem>): boolean => {
        return list !== undefined && list !== null && list.length > 0;
    };

    const updateSelectedYearFromApi = async (data: iCircleData, years: iEnergipresentationYearsModel) => {
        await updateSelectedYears(years, data);
    };

    const getCircleData = async (newYears: iEnergipresentationYearsModel, callback: Function) => {
        let apiRequest: iEnergipresentationApiRequestModel = {
            blockId: state.blockId,
            lastPublished: state.lastPublished
        };

        await DataHelper.FetchGraphData(apiRequest, callback, newYears);
    };

    const initializeCircle = (circleData: iCircleData, years: iEnergipresentationYearsModel) => {
        let newState = updateState(circleData, years);

        let minYearKey = newState.availableYears[0];
        let maxYearKey = newState.availableYears[newState.availableYears.length - 1];
        let minYear: number = minYearKey !== undefined && minYearKey !== null ? parseInt(minYearKey) : -1;
        let maxYear: number = maxYearKey !== undefined && maxYearKey !== null ? parseInt(maxYearKey) : -1;
        let currentYears: iEnergipresentationMinMaxYearsModel = { minYear: minYear, maxYear: maxYear };
        EventBus.dispatch("LoadedBlock_" + newState.blockId, currentYears);
    };

    const updateState = (circleData: iCircleData, years: iEnergipresentationYearsModel, isLoading: boolean = true): iEnergipresentationCircleModel => {
        var newState = { ...state };

        if (!circleData || !circleData.series || circleData.series.length === 0) {
            EventBus.dispatch("ToggleBlock_" + state.blockId, null);
            newState.year = (new Date(Date.now()).getFullYear() - 1) + "";
            newState.availableYears = [newState.year];
            setState(newState);
            return newState;
        }

        newState.year = years.firstYear.toString();
        newState.availableYears = circleData.series.map(s => s.name);
        circleData.chart.marginTop = 20;

        newState.series = [...circleData.series];

        circleData.series = circleData.series.filter(s => s.name === newState.year);
        newState.textReplacements = DisplayHelper.GetTextReplacementsFromData(circleData);
        newState.headTitle = DisplayHelper.ReflectDataInText(newState.headTitle, newState.textReplacements);
        circleData.title.text = null;

        newState.circleData = circleData;
        newState.isLoading = isLoading;

        setState(newState);
        if (delayed && newState.circleData.series.length) {
            EventBus.dispatch("GraphDataLoaded", newState.blockId);
        }
        return newState;
    };

    const setYears = async (newYears?: iEnergipresentationYearsModel) => {
        if (newYears === undefined || newYears === null) {
            newYears = { firstYear: -1, secondYear: -1 };
        }

        await getCircleData(newYears, initializeCircle);
    };

    const addSpecificHelpText = (cmsText: string): string => {
        if (!state.circleData?.series || !cmsText) {
            return cmsText;
        }
        let result = DisplayHelper.ReflectDataInText(cmsText, state.textReplacements);
        return result;
    };

    const getTableHeading = () => {
        return (DisplayHelper.ReflectDataInText(state.helpColumnHeaders, state.textReplacements, true)?.split(DisplayHelper.DataDelimiter) || ['', '%']) ?? [];
    };

    const getTable = (): string => {
        if (!state.circleData?.series) {
            return '';
        }
        let heading = getTableHeading();
        return DisplayHelper.ToHTMLTable(state.circleData.series, state.circleData.chart.type, state.headTitle, heading, state.decimals, '%', state.helpInvertTableAxis, state.helpColumnSortOrder);
    };

    return (
        <React.Fragment>
            <h3>{state.headTitle}</h3>
            <div className="circle-container">
                <div className="scroll-container">
                    {(state.circleData != null || delayed) ?
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={state.circleData}
                        />
                        :
                        <div>
                            <p><span className="label label-danger">Fel vid rendering av cirkeldiagram</span></p>
                        </div>
                    }
                </div>
            </div>
            <div className="button-wrapper">
                {(state.circleData != null || delayed) && (
                    <EnergipresentationOpenInNewTabButton
                        id={state.blockId}
                        data={state.circleData}
                        title={state.headTitle}
                        viewType={state.circleData?.chart?.type}
                        textReplacements={state.textReplacements}
                        columnHeaders={state.helpColumnHeaders}
                        invertTableAxis={state.helpInvertTableAxis}
                        helpTitle={state.helpTitle}
                        helpText={addSpecificHelpText(state.helpText)}
                        table={getTable()}
                        modelStyle={state.helpModelStyle}
                    />
                )}
                {(state.circleData != null || delayed) && (
                    <EnergipresentationExportButton
                        series={state.circleData?.series}
                        type={state.circleData?.chart?.type}
                        title={state.headTitle}
                        heading={[]}
                        unit={'%'}
                        sortOrder={state.helpColumnSortOrder}
                    />
                )}
            </div>

            {(state.circleData != null || delayed) && state.helpText && state.helpText != "" && (
                <EnergipresentationAccordion
                    key={"accordion_" + state.blockId}
                    title={state.helpTitle}
                    text={addSpecificHelpText(state.helpText)}
                    table={getTable()}
                    modelStyle={state.helpModelStyle}
                />
            )}
        </React.Fragment >
    );
};
export default EnergipresentationCircle;